/**
 * Whether the **store** holds a newer binary than the one running, and what the next tap does
 * about it (D266).
 *
 * **The container's update surface is empty by construction, not broken.**
 * `native/scripts/prepare-web-assets.mjs` deletes `sw.js` and its registration —
 * D161(e) refuses over-the-air JavaScript inside a store binary — so
 * `installShellUpdateCheck()`'s `report()` never fires, `shellUpdate.ts` stays at `available:
 * false`, and `Drawer`'s row, which renders only while a shell is waiting (D155), never appears.
 * A resident therefore holds whatever version they installed, indefinitely, with no signal and no
 * route to the listing. **This module is the replacement, and it replaces the *binary* rather than
 * the JavaScript** — the two are opposite things, and D161(e)'s strip is untouched and still
 * asserted (`native/test/shellContracts.test.ts`).
 *
 * **`action` crosses the bridge; it is never derived from `Capacitor.getPlatform()`.** Play answers
 * *update available* and owns a download; the App Store answers a version string and owns nothing.
 * Keeping that difference inside the per-platform plugin is the discipline `ble/nativeBluetooth.ts`
 * already keeps about the ASCII grammar, and it means a third platform later is a plugin rather
 * than a `switch` in P6's shell.
 *
 * **Read the injected global, never an import.** `web/` must not depend on `@capacitor/core`: the
 * shell is P6's and the container is P18's, and `native/` consumes the built bundle rather than the
 * other way round. `pwa/nativeContainer.ts`, `auth/nativeHttp.ts` and
 * `device-capabilities/contacts.ts` all say this about the same global.
 *
 * **`nativePromise`, not `Capacitor.Plugins.SeloGateStoreUpdate`.** Those proxies are built by
 * `registerPlugin` at *import* time, so in a bundle that refuses that import the namespace is empty
 * and the obvious-looking call is `undefined`.
 *
 * **This module knows nothing about the mute (D266 §3.5).** The preference governs the *report* and
 * lives in `updateAlerts.ts`, read by `useAppUpdate`; the check runs on its floor either way. Three
 * reasons, in that file — the shortest being that a download already in flight has to be allowed to
 * finish, or a muted resident strands a downloaded update on the device with no way to install it.
 */
import { isNativeContainer } from './nativeContainer'
import { installResumeRefresh } from '../device-cache/resume'
import { isNewerVersion } from './versionCompare'

/** How far the store rail has got. `idle` is also every failure, by design — see `check()`. */
export type StoreUpdatePhase = 'idle' | 'available' | 'downloading' | 'downloaded'

/**
 * What the next tap does. **The plugin decides this, not the shell.**
 *
 * `store` leaves the app for a listing (iOS); `download` starts Play's FLEXIBLE background
 * download; `restart` completes one that has finished. `none` is *nothing to do*.
 */
export type StoreUpdateAction = 'none' | 'store' | 'download' | 'restart'

export interface StoreUpdateState {
  readonly phase: StoreUpdatePhase
  readonly action: StoreUpdateAction
}

const IDLE: StoreUpdateState = { phase: 'idle', action: 'none' }

/**
 * Six hours, not `shellUpdate.ts`'s minute.
 *
 * That floor bounds a conditional GET of a ~2 KB `no-store` URL. This one bounds a Play IPC round
 * trip or an `itunes.apple.com` lookup, neither of which is ours and neither of which answers
 * anything that changes more than a few times a year. A store release is not a thing a resident
 * needs to hear about within a minute of it happening.
 */
const MIN_INTERVAL_MS = 6 * 60 * 60 * 1000

/**
 * How often `status()` is asked while a download is in flight. **It reads no network** — the
 * Android plugin's `InstallStateUpdatedListener` has already written the phase to a field, and this
 * only carries it across the bridge.
 */
const DOWNLOAD_POLL_MS = 2_000

/** The plugin name. `SeloGateStoreUpdatePlugin` on both platforms answers to exactly this string. */
const STORE_UPDATE_PLUGIN = 'SeloGateStoreUpdate'

interface CapacitorBridge {
  isNativePlatform?: () => boolean
  nativePromise?: (pluginName: string, methodName: string, options: unknown) => Promise<unknown>
}

let state: StoreUpdateState = IDLE
const listeners = new Set<() => void>()

function publish(next: StoreUpdateState): void {
  if (next.phase === state.phase && next.action === state.action) return
  state = next
  for (const listener of listeners) listener()
}

/** `useSyncExternalStore`'s two halves. `getStoreUpdateState` must return a stable reference. */
export function subscribeToStoreUpdate(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getStoreUpdateState(): StoreUpdateState {
  return state
}

/**
 * Test seam — return to "nothing available" between cases.
 *
 * **It notifies rather than dropping the listeners**, which is `shellUpdate.ts`'s recorded bug
 * ported rather than repeated: clearing the set silently unsubscribes every mounted component, so
 * the store keeps updating and the UI never moves again — a dot stuck on with an idle phase
 * underneath it. Harmless in a test that unmounts straight after, and exactly the shape of bug this
 * surface is least able to show you.
 */
export function __resetStoreUpdateState(): void {
  publish(IDLE)
}

function bridge(): CapacitorBridge | undefined {
  const capacitor = (globalThis as typeof globalThis & { Capacitor?: CapacitorBridge }).Capacitor
  // The two-term predicate `usesNativeBle()` uses, for the same reason: `isNativePlatform()` says
  // this is a container, and `nativePromise` says the bridge is actually wired. A browser fails the
  // first; a container whose bridge has not injected yet fails the second.
  if (!isNativeContainer()) return undefined
  if (typeof capacitor?.nativePromise !== 'function') return undefined
  return capacitor
}

/**
 * Read the plugin's answer into the two fields the shell speaks.
 *
 * **An unrecognised answer is IDLE, never an invented availability.** Both plugins resolve rather
 * than reject on every failure (§4, §5), so an object carrying neither field means the bridge is
 * not what we think it is — and defaulting it optimistically would put a row in the sidebar
 * pointing at a store that has nothing newer.
 *
 * **iOS's availability is decided here rather than in Swift** (§3.2): the plugin reports
 * `storeVersion` and `currentVersion` and this compares them. A plugin that answers `available`
 * directly is still honoured, which is Android's path — Play decided it, and there is no version
 * pair to read.
 */
export function storeStateOf(raw: unknown): StoreUpdateState {
  if (typeof raw !== 'object' || raw === null) return IDLE
  const answer = raw as {
    phase?: unknown
    action?: unknown
    storeVersion?: unknown
    currentVersion?: unknown
  }

  const action: StoreUpdateAction =
    answer.action === 'store' || answer.action === 'download' || answer.action === 'restart'
      ? answer.action
      : 'none'

  // The iOS shape: two version strings and no verdict. The comparator is the verdict.
  if (typeof answer.storeVersion === 'string' && typeof answer.currentVersion === 'string') {
    return isNewerVersion(answer.storeVersion, answer.currentVersion) && action !== 'none'
      ? { phase: 'available', action }
      : IDLE
  }

  const phase: StoreUpdatePhase =
    answer.phase === 'available' || answer.phase === 'downloading' || answer.phase === 'downloaded'
      ? answer.phase
      : 'idle'
  if (phase === 'idle' || action === 'none') return IDLE
  return { phase, action }
}

async function callPlugin(method: string): Promise<StoreUpdateState | undefined> {
  const capacitor = bridge()
  if (capacitor?.nativePromise === undefined) return undefined
  try {
    return storeStateOf(await capacitor.nativePromise(STORE_UPDATE_PLUGIN, method, {}))
  } catch {
    // **Swallowed on purpose, and this is where `ERROR_APP_NOT_OWNED` lands.** A build installed
    // by `adb` has no Play install record, so every developer build answers "no update" forever —
    // which is the correct behaviour (the row is absent, exactly as today) and is also why a device
    // pass on a sideloaded build proves nothing about this feature (`native/TESTING.md`). An
    // offline resume is the other caller of this branch, and it must be equally silent: D129's
    // posture is that a network failure is offline-read-only, never an event.
    return undefined
  }
}

/**
 * The live installation's download poll, so `applyStoreUpdate()` can start the one this module
 * already owns rather than minting a second timer beside it.
 *
 * `undefined` in a browser and between installs. In the app there is exactly one installation,
 * made at module scope in `main.tsx` and never torn down.
 */
let activePoll: (() => void) | undefined

/**
 * Installs the check and returns its teardown.
 *
 * **Costs a browser nothing.** `bridge()` fails on its first term outside a container, so this
 * returns before registering anything at all — which is why `main.tsx` calls it unconditionally.
 */
export function installStoreUpdateCheck(): () => void {
  if (bridge() === undefined) return () => undefined

  let installed = true
  let inFlight = false
  let pollTimer: ReturnType<typeof setTimeout> | undefined

  /**
   * Ask the plugin for a phase it already holds, until the download settles.
   *
   * **Polled rather than evented, and that is a posture rather than a shortcut.** A
   * `notifyListeners` event would need `Capacitor.nativeCallback` plus manual `callbackId`
   * bookkeeping for removal — a second injected primitive with no precedent in this repository —
   * to carry one transition. This reads a field the Android listener maintains anyway, it runs
   * **only** between `apply()` and the download settling, and it never runs in a browser. It is
   * also what keeps `@capacitor/app` absent, which `shellContracts.test.ts` asserts.
   */
  function poll(): void {
    clearTimeout(pollTimer)
    pollTimer = setTimeout(() => {
      if (!installed) return
      void callPlugin('status').then((next) => {
        if (!installed || next === undefined) return
        publish(next)
        // Stops on `downloaded`, on a failure the plugin mapped back to `available`, and on
        // teardown. A poll that kept running past the settle would be a timer with no reader.
        if (next.phase === 'downloading') poll()
      })
    }, DOWNLOAD_POLL_MS)
  }

  /** Returns whether it actually asked — `installResumeRefresh` consumes its window on a `true`. */
  function check(): boolean {
    // **The returned boolean is load-bearing** (`resume.ts`): a declined resume that stamped the
    // clock would silently suppress the *next* one, so an app resumed with a check already in
    // flight would then say nothing for six hours.
    if (!installed || inFlight) return false
    // A download in flight is already being watched by the poll, and asking the store about an
    // update it is mid-way through delivering answers a question nobody is asking.
    if (state.phase === 'downloading') return false
    if (bridge() === undefined) return false

    inFlight = true
    void callPlugin('check')
      .then((next) => {
        if (!installed || next === undefined) return
        publish(next)
      })
      .finally(() => {
        inFlight = false
      })
    return true
  }

  /**
   * **The startup check is not optional.** `installResumeRefresh` stamps its clock at install
   * rather than leaving it at zero, so without this call the first six hours after every launch
   * are silent — and a resident who launches the app, uses it and closes it again inside that
   * window would never be told anything at all.
   */
  check()
  const teardownResume = installResumeRefresh(check, { minIntervalMs: MIN_INTERVAL_MS })
  activePoll = poll

  return () => {
    installed = false
    clearTimeout(pollTimer)
    if (activePoll === poll) activePoll = undefined
    teardownResume()
  }
}

/**
 * The tap. **Called from exactly one place** — `useAppUpdate`'s `apply()`, which is the sidebar's
 * Update row.
 *
 * The three actions end differently and none of them is a reload of our own:
 *
 * - `download` — Play's FLEXIBLE consent dialog, then a background download. Nothing restarts and
 *   the app stays usable, so the row goes to *Updating…* and the poll carries it to
 *   *Restart to finish*. **That second tap is a second consent**, which is what makes this stronger
 *   than the PWA rail rather than a bending of D137: there, one tap buys both `SKIP_WAITING` and a
 *   `location.reload()`.
 * - `restart` — `completeUpdate()`. Nothing is published: the process is leaving.
 * - `store` — the App Store listing opens over us. Nothing is published either, but for the
 *   opposite reason: **the app is being backgrounded rather than replaced, and a resident who comes
 *   back without updating must still find a live row.**
 */
export async function applyStoreUpdate(): Promise<void> {
  const action = state.action
  if (action === 'none') return

  await callPlugin('apply')

  if (action === 'download') {
    // **Optimistic, and deliberately not conditional on the plugin's answer.** The consent dialog
    // is Play's, so `apply()` resolves when it is raised rather than when the download begins; a
    // row that waited for a confirmation would look ignored for as long as that dialog stood. The
    // listener corrects it either way — a cancelled dialog reports `available` back through the
    // poll, which is the same shape as a dismissed install prompt.
    publish({ phase: 'downloading', action: 'restart' })
    activePoll?.()
  }
}
